/* src/Components/MessagePreview.js */
import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { db } from '../firebaseConfig';
import {
  collection, query, where, onSnapshot,
  orderBy, addDoc, serverTimestamp, updateDoc, doc
} from 'firebase/firestore';
import { FiX } from 'react-icons/fi'; // Close icon
import ProfileIcon from './ProfileIcon';

// --- Icons & Helpers ---

const PaperAirplaneIcon = () => (
  <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
  </svg>
);

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate();
  const now = new Date();

  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }

  return date.toLocaleDateString('en-US');
};

// --- Sub-Components ---

const ConversationListItem = ({ name, lastMessage, timestamp, isActive, onClick }) => (
  <div
    onClick={onClick}
    className={`
      flex items-center p-3 cursor-pointer transition-colors
      ${isActive
        ? 'bg-blue-50 border-l-4 border-blue-600' // <-- TWEAK 1: Active state
        : 'hover:bg-gray-50' // <-- No border on inactive
      }
    `}
  >
    {/* TWEAK 2: USE ProfileIcon component (with custom size) */}
    <ProfileIcon name={name} className="w-9 h-9 text-sm mr-3" />

    <div className="flex-1 min-w-0">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-gray-900 truncate">{name}</h3>
        <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{timestamp}</span>
      </div>
      <p className="text-sm text-gray-500 truncate">{lastMessage}</p>
    </div>
  </div>
);

const MessageBubble = ({ text, isSent }) => (
  <div className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}>
    <div
      className={`
        max-w-xs lg:max-w-md px-4 py-2 rounded-2xl shadow
        {/* This is the gray bubble for received messages */}
        ${isSent ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'}
      `}
    >
      <p className="text-sm">{text}</p>
    </div>
  </div>
);

const CONVERSATIONS_PATH = `/artifacts/${'default-app-id'}/public/data/conversations`;

// --- Main MessagePreview Component ---

const MessagePreview = () => {
  const { user } = useContext(AuthContext);
  const userId = user?._id || user?.id;
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(true);
  const [error, setError] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [activePreviewConvoId, setActivePreviewConvoId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState("");

  // --- Real-time Conversation List Listener ---
  useEffect(() => {
    if (!userId) {
      setLoadingConvos(false);
      return;
    };

    setLoadingConvos(true);
    const convoCollection = collection(db, CONVERSATIONS_PATH);
    const q = query(convoCollection, where('participants', 'array-contains', userId));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const convos = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      convos.sort((a, b) => {
        const timeA = a.lastMessageTimestamp?.toMillis() || 0;
        const timeB = b.lastMessageTimestamp?.toMillis() || 0;
        return timeB - timeA;
      });

      setConversations(convos);
      setLoadingConvos(false);

      if (convos.length > 0) {
        if (convos[0].lastMessageSenderId !== userId) {
          setHasUnread(true);
        }
        if (!activePreviewConvoId) {
          setActivePreviewConvoId(currentId => currentId ? currentId : convos[0].id);
        }
      }

    }, (err) => {
      console.error("Error fetching conversations:", err);
      setError("Could not load conversations.");
      setLoadingConvos(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // --- Real-time Message Listener (for active chat) ---
  useEffect(() => {
    if (!activePreviewConvoId) {
      setMessages([]);
      return;
    }

    setLoadingMessages(true);
    const messagesCollection = collection(db, CONVERSATIONS_PATH, activePreviewConvoId, 'messages');
    const q = query(messagesCollection, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const msgs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(msgs);
      setLoadingMessages(false);
    }, (err) => {
      console.error("Error fetching messages:", err);
      setLoadingMessages(false);
    });

    return () => unsubscribe();
  }, [activePreviewConvoId]);

  // --- Auto-scroll to latest message ---
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // --- Send Message Handler ---
  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || !userId || !activePreviewConvoId) return;

    try {
      setNewMessage("");
      const messagesCollection = collection(db, CONVERSATIONS_PATH, activePreviewConvoId, 'messages');
      const messageData = {
        text: text,
        senderId: userId,
        timestamp: serverTimestamp(),
      };
      await addDoc(messagesCollection, messageData);

      const convoDoc = doc(db, CONVERSATIONS_PATH, activePreviewConvoId);
      await updateDoc(convoDoc, {
        lastMessageText: text,
        lastMessageTimestamp: serverTimestamp(),
        lastMessageSenderId: userId,
      });

    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  // --- Event Handlers ---
  const onOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setHasUnread(false);
    }
  };

  const handleSelectConversation = (convoId) => {
    setActivePreviewConvoId(convoId);
  };

  const limitedConversations = conversations.slice(0, 5);

  // --- Memos for Active Chat Info ---
  const activeConversation = useMemo(() => {
    return conversations.find(c => c.id === activePreviewConvoId);
  }, [activePreviewConvoId, conversations]);

  const otherParticipantId = useMemo(() => {
    return activeConversation?.participants.find(p => p !== userId);
  }, [activeConversation, userId]);

  const otherParticipantName = useMemo(() => {
    if (!activeConversation || !otherParticipantId) return "Chat";
    return activeConversation.participantInfo?.[otherParticipantId] || "Tasker";
  }, [activeConversation, otherParticipantId]);

  const triggerButtonClasses =
    "relative text-gray-900 font-medium py-2 px-3 transition-all duration-300 hover:text-blue-600 hover:scale-105 cursor-pointer";

  // --- Main Render ---
  return (
    <div className="relative z-50" ref={dropdownRef}>

      {/* The "Messages" Button */}
      <button
        onClick={onOpen}
        className={triggerButtonClasses}
      >
        Messages
        {hasUnread && (
          <span className="absolute top-1 right-0 block h-2.5 w-2.5 rounded-full bg-blue-600 ring-2 ring-white" />
        )}
      </button>

      {/* Backdrop Portal */}
      {isOpen && createPortal(
        <div
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm"
          aria-hidden="true"
        ></div>,
        document.body
      )}

      {/* Modal Portal */}
      {isOpen && createPortal(
        <div
          className="
            fixed top-20 right-10 z-50 bg-white border border-gray-200 shadow-xl 
            w-[56rem] 
            h-[32rem] 
            rounded-xl
            flex flex-row overflow-hidden
          "
        >
          {/* Close button */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 z-10"
            aria-label="Close chat"
          >
            <FiX className="w-5 h-5" />
          </button>

          {/* --- LEFT PANEL: Conversation List --- */}
          <div className="flex flex-col w-1/3 h-full border-r border-gray-200">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 text-lg">Recent Messages</h3>
            </div>

            {/* List */}
            <div className="flex-1 divide-y divide-gray-100 overflow-y-auto">
              {loadingConvos && (
                <p className="p-6 text-sm text-center text-gray-500">Loading...</p>
              )}
              {!loadingConvos && error && (
                <p className="p-6 text-sm text-center text-red-500">{error}</p>
              )}

              {!loadingConvos && limitedConversations.length > 0 ? (
                limitedConversations.map(convo => {
                  const otherId = convo.participants.find(p => p !== userId);
                  const name = convo.participantInfo?.[otherId] || "Unknown User";
                  const lastMsg = convo.lastMessageText || "No messages yet";
                  const timestamp = formatTimestamp(convo.lastMessageTimestamp);

                  return (
                    <ConversationListItem
                      key={convo.id}
                      name={name}
                      lastMessage={lastMsg}
                      timestamp={timestamp}
                      isActive={convo.id === activePreviewConvoId}
                      onClick={() => handleSelectConversation(convo.id)}
                    />
                  );
                })
              ) : (
                !loadingConvos && !error && (
                  <p className="p-6 text-sm text-center text-gray-500">
                    No messages yet.
                  </p>
                )
              )}
            </div>

            {/* Footer Link */}
            <Link
              to="/chat"
              onClick={() => setIsOpen(false)}
              className="block text-center p-3 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-blue-600 rounded-bl-lg border-t border-gray-200"
            >
              View All Messages
            </Link>
          </div>

          {/* --- RIGHT PANEL: Active Chat --- */}
          <div className="flex flex-col w-2/3 h-full bg-gray-50">
            {!activePreviewConvoId ? (
              <div className="m-auto text-center text-gray-500">
                <p>Select a conversation to chat.</p>
              </div>
            ) : (
              <>
                {/* Chat Header (slimmed down) */}
                <div className="flex items-center p-2 bg-white border-b border-gray-200 shadow-sm">
                  <ProfileIcon
                    name={otherParticipantName}
                    className="w-9 h-9 text-sm mr-3"
                  />
                  <h2 className="text-lg font-semibold text-gray-900">{otherParticipantName}</h2>
                </div>

                {/* Message Area */}
                <div className="flex-1 p-4 overflow-y-auto space-y-4">
                  {loadingMessages && <p className="text-center text-gray-500">Loading messages...</p>}
                  {messages.map((msg) => (
                    <MessageBubble
                      key={msg.id}
                      text={msg.text}
                      isSent={msg.senderId === userId}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-gray-200">
                  <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 bg-transparent border-none outline-none text-gray-800"
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="ml-3 p-2 rounded-full bg-blue-600 text-white disabled:bg-gray-400 transition-colors"
                    >
                      <PaperAirplaneIcon />
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
        , document.body
      )}
    </div>
  );
};

export default MessagePreview;